import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, User, Menu, X, ChevronDown } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/api";
import LaunchBanner from "@/components/LaunchBanner";

// Official Collections — exact names, do not shorten.
const OFFICIAL_COLLECTIONS = [
  { slug: "the-doodles-official-collection", label: "The Doodles Official Collection" },
  { slug: "military-official-collection", label: "Military Official Collection" },
  { slug: "one-united-official-collection", label: "One United Official Collection" },
  { slug: "childrens-official-collection", label: "Children\u2019s Official Collection" },
  { slug: "seniors-collection", label: "Seniors Collection" },
  { slug: "the-encore-official-collection", label: "The Encore Official Collection" },
  { slug: "graphics-official-collection", label: "Graphics Official Collection" },
  { slug: "sports-official-collection", label: "Sports Official Collection" },
  { slug: "spine-official-collection", label: "Spine Official Collection" },
];

const SECONDARY_NAV = [
  { to: "/custom-design", label: "Custom" },
  { to: "/cleaning", label: "Cleaning" },
  { to: "/logistics", label: "Logistics" },
];

export function Header() {
  const nav = useNavigate();
  const { items } = useCart();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const count = items.reduce((s, i) => s + i.qty, 0);
  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-neutral-200 bg-[color:var(--hb-cream)]/85 backdrop-blur-xl" data-testid="site-header">
      {/* Admin-toggleable launch banner (hidden unless enabled from Admin) */}
      <LaunchBanner />
      {/* Announcement bar */}
      <div className="bg-[color:var(--hb-navy)] text-white text-xs tracking-widest uppercase py-2 text-center font-medium">
        <span className="opacity-90">United in purpose · Built on family · Free shipping over $75</span>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-20">
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-link">
            <img src={BRAND.logo} alt="Hampton Blu" className="h-12 w-12 object-contain" />
            <div className="hidden sm:block leading-none">
              <div className="font-display text-2xl text-[color:var(--hb-navy)]">HAMPTON BLU</div>
              <div className="text-[10px] tracking-[0.35em] text-neutral-500 mt-1">CREATIONS · THE RIGHT WAY</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7 text-sm font-medium">
            <div className="relative group" data-testid="nav-shop-dropdown">
              <button className="flex items-center gap-1 text-neutral-700 hover:text-[color:var(--hb-royal)] transition-colors py-6" data-testid="nav-shop-trigger">
                Shop <ChevronDown size={14} className="opacity-60 group-hover:rotate-180 transition-transform" />
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-80 bg-white border border-neutral-200 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none group-hover:pointer-events-auto">
                <div className="py-2">
                  <div className="px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-neutral-400 border-b border-neutral-100">Official Collections</div>
                  {OFFICIAL_COLLECTIONS.map(c => (
                    <Link key={c.slug} to={`/collections/${c.slug}`}
                      className="block px-4 py-2 text-sm text-neutral-700 hover:bg-[color:var(--hb-warm)] hover:text-[color:var(--hb-royal)] transition-colors"
                      data-testid={`nav-collection-${c.slug}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {SECONDARY_NAV.map(n => (
              <Link key={n.to} to={n.to} className="text-neutral-700 hover:text-[color:var(--hb-royal)] transition-colors" data-testid={`nav-${n.label.toLowerCase().replace(/ /g,'-')}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={onSearch} className="hidden md:flex items-center bg-white border border-neutral-200 px-3 h-10 w-56">
              <Search size={16} className="text-neutral-500" />
              <input
                value={q} onChange={e=>setQ(e.target.value)}
                placeholder="Search designs, hoodies…"
                className="bg-transparent px-2 text-sm w-full focus:outline-none"
                data-testid="header-search-input"
              />
            </form>
            <Link to="/wishlist" aria-label="Wishlist" className="p-2 hover:text-[color:var(--hb-royal)]" data-testid="header-wishlist-btn">
              <Heart size={20} />
            </Link>
            <Link to={user ? "/account" : "/auth"} aria-label="Account" className="p-2 hover:text-[color:var(--hb-royal)]" data-testid="header-account-btn">
              <User size={20} />
            </Link>
            <Link to="/cart" aria-label="Cart" className="relative p-2 hover:text-[color:var(--hb-royal)]" data-testid="header-cart-btn">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[color:var(--hb-royal)] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count-badge">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-neutral-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 py-2">Official Collections</div>
            {OFFICIAL_COLLECTIONS.map(c => (
              <Link key={c.slug} to={`/collections/${c.slug}`} onClick={()=>setMobileOpen(false)}
                className="block text-sm py-2 border-b border-neutral-100" data-testid={`mobile-collection-${c.slug}`}>
                {c.label}
              </Link>
            ))}
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 py-2 pt-4">More</div>
            {SECONDARY_NAV.map(n => (
              <Link key={n.to} to={n.to} onClick={()=>setMobileOpen(false)} className="block text-sm py-2 border-b border-neutral-100">{n.label}</Link>
            ))}
            <form onSubmit={onSearch} className="pt-3">
              <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" className="w-full border border-neutral-200 px-3 py-2 text-sm" data-testid="mobile-search-input" />
            </form>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[color:var(--hb-navy)] text-white/90 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img src={BRAND.logo} alt="Hampton Blu" className="h-12 w-12" />
            <div>
              <div className="font-display text-2xl">HAMPTON BLU</div>
              <div className="text-[10px] tracking-[0.35em] opacity-70">CREATIONS</div>
            </div>
          </div>
          <p className="text-sm text-white/70 max-w-md leading-relaxed">
            United in purpose. Built on family. Empowering futures. Hampton Blu Creations designs premium custom apparel for the ones who wear their story.
          </p>
          <NewsletterInline />
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-white/50 mb-4">Official Collections</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/collections/the-doodles-official-collection" data-testid="footer-collection-doodles">The Doodles Official Collection</Link></li>
            <li><Link to="/collections/military-official-collection" data-testid="footer-collection-military">Military Official Collection</Link></li>
            <li><Link to="/collections/one-united-official-collection" data-testid="footer-collection-oneunited">One United Official Collection</Link></li>
            <li><Link to="/collections/childrens-official-collection" data-testid="footer-collection-childrens">Children&rsquo;s Official Collection</Link></li>
            <li><Link to="/collections/seniors-collection" data-testid="footer-collection-seniors">Seniors Collection</Link></li>
            <li><Link to="/collections/the-encore-official-collection" data-testid="footer-collection-encore">The Encore Official Collection</Link></li>
            <li><Link to="/collections/graphics-official-collection" data-testid="footer-collection-graphics">Graphics Official Collection</Link></li>
            <li><Link to="/collections/sports-official-collection" data-testid="footer-collection-sports">Sports Official Collection</Link></li>
            <li><Link to="/collections/spine-official-collection" data-testid="footer-collection-spine">Spine Official Collection</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-white/50 mb-4">Divisions</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/">Creations (Apparel)</Link></li>
            <li><Link to="/cleaning" data-testid="footer-cleaning-link">Cleaning Services</Link></li>
            <li><Link to="/logistics" data-testid="footer-logistics-link">Logistics</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs tracking-widest uppercase text-white/50 mb-4">Help</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about">About</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><Link to="/custom-design">Custom Design Request</Link></li>
            <li><Link to="/track">Track Order</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/50">
        © {new Date().getFullYear()} Hampton Blu Creations. All rights reserved.
      </div>
    </footer>
  );
}

function NewsletterInline() {
  const [email, setEmail] = useState("");
  const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { api } = await import("@/lib/api");
      await api.post("/newsletter", { email });
      setOk(true); setEmail("");
    } catch {}
  };
  return (
    <form onSubmit={submit} className="mt-6 flex gap-2 max-w-sm" data-testid="footer-newsletter-form">
      <input type="email" required value={email} onChange={e=>setEmail(e.target.value)}
        placeholder="Your email" className="flex-1 bg-white/10 border border-white/20 px-3 py-2 text-sm placeholder:text-white/50 focus:outline-none"
        data-testid="footer-newsletter-input" />
      <button className="bg-white text-[color:var(--hb-navy)] px-4 text-xs font-semibold tracking-widest uppercase" data-testid="footer-newsletter-submit">
        {ok ? "Subscribed" : "Join"}
      </button>
    </form>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
